/**
 * Add Share Class Modal
 *
 * Modal for adding or editing a share class (aktieslag).
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { useCreateShareClass, useUpdateShareClass } from "@/hooks/use-share-book";
import type { ShareClass } from "@/lib/api";

interface AddShareClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  editingClass?: ShareClass | null;
}

const shareClassSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  code: z.string().min(1, "Kod krävs").max(5, "Max 5 tecken"),
  votingPower: z.string().refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    "Röststyrka måste vara större än 0"
  ),
  parValue: z.string().optional(),
  isPreference: z.boolean(),
  preferenceDividend: z.string().optional(),
  preferenceDetails: z.string().optional(),
  totalShares: z.string().min(1, "Antal aktier krävs").refine(
    (val) => !isNaN(Number(val)) && Number(val) >= 0 && Number.isInteger(Number(val)),
    "Antal aktier måste vara ett heltal"
  ),
  numberSeriesStart: z.string().optional(),
});

type ShareClassFormData = z.infer<typeof shareClassSchema>;

export function AddShareClassModal({
  isOpen,
  onClose,
  companyId,
  editingClass,
}: AddShareClassModalProps) {
  const createShareClass = useCreateShareClass();
  const updateShareClass = useUpdateShareClass();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ShareClassFormData>({
    resolver: zodResolver(shareClassSchema),
    defaultValues: {
      name: "",
      code: "",
      votingPower: "1",
      parValue: "",
      isPreference: false,
      preferenceDividend: "",
      preferenceDetails: "",
      totalShares: "0",
      numberSeriesStart: "1",
    },
  });

  const isPreference = watch("isPreference");

  // Reset form when modal opens/closes or editing class changes
  useEffect(() => {
    if (isOpen) {
      if (editingClass) {
        reset({
          name: editingClass.name,
          code: editingClass.code,
          votingPower: editingClass.votingPower,
          parValue: editingClass.parValue || "",
          isPreference: editingClass.isPreference,
          preferenceDividend: editingClass.preferenceDividend || "",
          preferenceDetails: editingClass.preferenceDetails || "",
          totalShares: String(editingClass.totalShares),
          numberSeriesStart: String(editingClass.numberSeriesStart),
        });
      } else {
        reset({
          name: "",
          code: "",
          votingPower: "1",
          parValue: "",
          isPreference: false,
          preferenceDividend: "",
          preferenceDetails: "",
          totalShares: "0",
          numberSeriesStart: "1",
        });
      }
    }
  }, [isOpen, editingClass, reset]);

  const onSubmit = async (data: ShareClassFormData) => {
    try {
      const payload = {
        name: data.name,
        code: data.code,
        votingPower: Number(data.votingPower),
        parValue: data.parValue ? Number(data.parValue) : undefined,
        isPreference: data.isPreference,
        preferenceDividend: data.isPreference && data.preferenceDividend ? Number(data.preferenceDividend) : undefined,
        preferenceDetails: data.isPreference && data.preferenceDetails ? data.preferenceDetails : undefined,
        totalShares: Number(data.totalShares),
        numberSeriesStart: data.numberSeriesStart ? Number(data.numberSeriesStart) : 1,
      };

      if (editingClass) {
        await updateShareClass.mutateAsync({
          shareClassId: editingClass.id,
          data: payload,
          companyId,
        });
      } else {
        await createShareClass.mutateAsync({
          companyId,
          data: payload,
        });
      }

      toast.success(editingClass ? "Aktieslaget har uppdaterats" : "Aktieslaget har skapats");
      reset();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte spara aktieslaget";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingClass ? "Redigera aktieslag" : "Lägg till aktieslag"}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic info */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Namn *
            </label>
            <input
              type="text"
              {...register("name")}
              className="input"
              placeholder="Stamaktie, A-aktie, etc."
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kod *
            </label>
            <input
              type="text"
              {...register("code")}
              className="input"
              placeholder="STAM, A, B, etc."
              maxLength={5}
            />
            {errors.code && (
              <p className="text-sm text-red-600 mt-1">{errors.code.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Max 5 tecken</p>
          </div>
        </div>

        {/* Voting and value */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Röststyrka per aktie *
            </label>
            <input
              type="text"
              {...register("votingPower")}
              className="input"
              placeholder="1"
            />
            {errors.votingPower && (
              <p className="text-sm text-red-600 mt-1">{errors.votingPower.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              ABL 4:5 tillåter max 10:1 röstvärdesdifferens
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kvotvärde (SEK)
            </label>
            <input
              type="text"
              {...register("parValue")}
              className="input"
              placeholder="Frivilligt"
            />
            <p className="text-xs text-gray-500 mt-1">
              Beräknas automatiskt om tomt
            </p>
          </div>
        </div>

        {/* Share count and series */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Totalt antal aktier *
            </label>
            <input
              type="text"
              {...register("totalShares")}
              className="input"
              placeholder="1000"
            />
            {errors.totalShares && (
              <p className="text-sm text-red-600 mt-1">{errors.totalShares.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nummerseriens start
            </label>
            <input
              type="text"
              {...register("numberSeriesStart")}
              className="input"
              placeholder="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Från vilket nummer aktienumreringen börjar
            </p>
          </div>
        </div>

        {/* Preference shares */}
        <div className="border-t border-gray-200 pt-4">
          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              {...register("isPreference")}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Detta är en preferensaktie
            </span>
          </label>

          {isPreference && (
            <div className="space-y-4 pl-6 border-l-2 border-blue-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferensutdelning (SEK/aktie/år)
                </label>
                <input
                  type="text"
                  {...register("preferenceDividend")}
                  className="input"
                  placeholder="10.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivning av preferensvillkor
                </label>
                <textarea
                  {...register("preferenceDetails")}
                  className="input min-h-[80px]"
                  placeholder="Beskriv preferensvillkoren enligt bolagsordningen..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>OBS:</strong> Vid ändring av antal aktier bör detta göras genom att
            registrera en transaktion (nyemission, split, etc.) för korrekt spårbarhet.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sparar...
              </>
            ) : editingClass ? (
              "Spara ändringar"
            ) : (
              "Lägg till aktieslag"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
